import { Data, Utils } from 'common';
import { useEffect, useMemo, useRef, useState } from 'react';
import UsersUtils from '../../users/Utils';
import AccountCommunication from '../../utils/AccountCommunication';
import {
	getIncludedSeats,
	getSeatPrice,
	isMultiLocationPlan,
	isPaygPlan,
	NEW_LINEUP_PLAN_IDS,
} from '../planRules';

const { BillingPlans, Communication, Session, Analytics, Logger } = Utils;
const { PlanIds } = Data;

export interface Plan {
	PlanId: number;
	Name: string;
	Price: number;
	PricePeriod?: string;
	Summary?: string;
	Description?: string;
	DisplayName?: string;
	Tagline?: string;
	InheritLabel?: string;
	LearnMoreUrl?: string;
	MaxAllowedUsers?: number;
	AdditionalUserFee?: number;
	NumberOfLocations?: number;
	StampsPrintLimit?: number;
}

export type ConfirmationState = 'idle' | 'confirming' | 'success';

export interface User {
	UserId: number;
	UserName: string;
	FirstName: string;
	LastName: string;
	EmailAddress: string;
	RoleLabel?: string;
}

export interface SubscriptionPlanState {
	plans: Plan[];
	currentPlanId: number;
	currentSeats: number;
	activeUsers: number;
	users: User[];
	nextBillingDate: string;
	selectedPlanId: number;
	seatsByPlan: Record<number, number>;
	isLoading: boolean;
	confirmationState: ConfirmationState;
}

export interface SubscriptionPlanActions {
	selectPlan: (planId: number) => void;
	setSeats: (planId: number, count: number) => void;
	resetToCurrentPlan: () => void;
	confirmPlanChange: (activeUsersOverride?: number) => Promise<boolean>;
	deleteUsersAndConfirm: (userIds: number[]) => Promise<void>;
}

export interface SubscriptionPlanDerived {
	hasChanges: boolean;
	isOverflow: boolean;
	isSelectedMultiLocation: boolean;
	isUpgrade: boolean;
	isDowngrade: boolean;
	isSeatAdjustedAfterPlanChange: boolean;
	isOnTrialPlan: boolean;
	isOnProfessional: boolean;
	pendingBasicOnTrial: boolean;
	currentIsBasic: boolean;
	pendingIsMultiLocation: boolean;
	currentIsLegacy: boolean;
	currentPlanName: string;
	selectedPlan: Plan | undefined;
	selectedSeats: number;
	includedSeats: number;
	additionalSeats: number;
	totalPrice: number;
	seatPrice: number;
}

const initSeatsByPlan = (
	plans: Plan[],
	currentPlanId: number,
	currentSeats: number
): Record<number, number> => {
	const seatsByPlan: Record<number, number> = {};
	plans.forEach(plan => {
		const included = getIncludedSeats(plan);
		seatsByPlan[plan.PlanId] =
			plan.PlanId === currentPlanId ? currentSeats : included;
	});
	return seatsByPlan;
};

export const useSubscriptionPlanState = (): {
	state: SubscriptionPlanState;
	actions: SubscriptionPlanActions;
	derived: SubscriptionPlanDerived;
	currentPlanCardRef: React.RefObject<HTMLDivElement | null>;
} => {
	const {
		planDetails = {} as any,
		nextBillingDate = '',
		closeRequestedDate,
	} = Session.getAccountInfo();
	const isCancelled = Boolean(closeRequestedDate);
	const { planId: actualCurrentPlanId, pendingPlanId: rawPendingPlanId } =
		planDetails;
	// pendingPlanId arrives as a SKU (e.g. 1020403); subtract the base to get the real plan ID.
	const sessionPendingPlanId = rawPendingPlanId
		? rawPendingPlanId - 1020000
		: null;
	// Optimistic override: after a successful plan change the session takes a moment to
	// propagate, so we keep a local pending plan until the next session reload reflects it.
	const [optimisticPendingPlanId, setOptimisticPendingPlanId] = useState<
		number | null
	>(null);
	// Once the session catches up to (or past) the optimistic value, drop the override.
	useEffect(() => {
		if (
			optimisticPendingPlanId !== null &&
			sessionPendingPlanId === optimisticPendingPlanId
		) {
			setOptimisticPendingPlanId(null);
		}
	}, [optimisticPendingPlanId, sessionPendingPlanId]);
	const effectivePendingPlanId =
		optimisticPendingPlanId ?? sessionPendingPlanId;
	const pendingPlanId = effectivePendingPlanId;
	// Treat the pending plan as the effective current plan when one exists —
	// except when the user is still on Trial 401 and the pending plan was
	// auto-selected at registration. In that case the user hasn't actually
	// committed to the pending plan yet, so we keep Trial as the current plan.
	const pendingIsAutoSelectedOnTrial =
		actualCurrentPlanId === PlanIds.Trial401 &&
		BillingPlans.pendingPlanWasAutoSelectedOnRegistration;
	// On a legacy current plan, keep the "Current" badge on the legacy card
	// rather than promoting any pending new-lineup plan to current.
	const currentIsLegacy =
		actualCurrentPlanId != null &&
		NEW_LINEUP_PLAN_IDS.indexOf(actualCurrentPlanId) === -1;
	const currentPlanId =
		pendingPlanId && !pendingIsAutoSelectedOnTrial
			? pendingPlanId
			: actualCurrentPlanId;

	const allPlans: Plan[] = BillingPlans.plans || [];
	const sortedPlans = useMemo(() => {
		const visiblePlans = allPlans.filter(plan => {
			if (isPaygPlan(plan.PlanId)) return plan.PlanId === currentPlanId;
			// Hide the Trial plan once a pending plan is set — the user has already moved on.
			if (plan.PlanId === PlanIds.Trial401 && pendingPlanId) return false;
			return true;
		});
		// Sort legacy plans before new-lineup plans so the user's current legacy
		// plan sits on the left of the grid; tie-break by price ascending.
		return [...visiblePlans].sort((a: Plan, b: Plan) => {
			const aLegacy = NEW_LINEUP_PLAN_IDS.indexOf(a.PlanId) === -1;
			const bLegacy = NEW_LINEUP_PLAN_IDS.indexOf(b.PlanId) === -1;
			if (aLegacy !== bLegacy) return aLegacy ? -1 : 1;
			return a.Price - b.Price;
		});
	}, [allPlans, currentPlanId, pendingPlanId]);

	const [activeUsers, setActiveUsers] = useState(0);
	const [users, setUsers] = useState<User[]>([]);
	const [currentSeats, setCurrentSeats] = useState(() => {
		const currentPlan = sortedPlans.find(
			(p: Plan) => p.PlanId === currentPlanId
		);
		return getIncludedSeats(currentPlan);
	});

	// Default the selection to Professional when:
	//   - the user is on Trial with no pending plan, OR
	//   - the user is on Trial with a pending plan auto-selected at registration
	//   - the account is cancelled (any plan acts as a reactivation pick — nudge
	//     the user toward Pro)
	// (so the summary prompts the user to confirm the plan post-trial).
	const isOnTrial = BillingPlans.isNewLineUpTrialPlan(actualCurrentPlanId);
	const hasProfessionalAvailable = sortedPlans.some(
		p => p.PlanId === PlanIds.Professional403
	);
	const shouldDefaultToProfessional =
		hasProfessionalAvailable &&
		(isCancelled ||
			(isOnTrial &&
				(!pendingPlanId ||
					BillingPlans.pendingPlanWasAutoSelectedOnRegistration)));
	const initialSelectedPlanId = shouldDefaultToProfessional
		? PlanIds.Professional403
		: currentPlanId;
	const [selectedPlanId, setSelectedPlanId] = useState(initialSelectedPlanId);
	const [seatsByPlan, setSeatsByPlan] = useState(() =>
		initSeatsByPlan(sortedPlans, currentPlanId, currentSeats)
	);
	const [isLoading, setIsLoading] = useState(false);
	const [confirmationState, setConfirmationState] =
		useState<ConfirmationState>('idle');

	const currentPlanCardRef = useRef<HTMLDivElement | null>(null);

	// Fetch seat data from API on mount
	useEffect(() => {
		const fetchSeatData = async () => {
			try {
				// Fetch max allowed users (total seats owned)
				const maxResponse = await AccountCommunication.getMaxAllowedUsers();
				if ((Communication as any).isSuccessful(maxResponse)) {
					const currentPlan = sortedPlans.find(
						(p: Plan) => p.PlanId === currentPlanId
					);
					const maxSeats =
						typeof maxResponse.data === 'number'
							? maxResponse.data
							: getIncludedSeats(currentPlan);
					setCurrentSeats(maxSeats);
					setSeatsByPlan(prev => ({
						...prev,
						[currentPlanId]: maxSeats,
					}));
				}

				// Fetch users list to determine active user count
				const usersResponse = await AccountCommunication.getUsers();
				if ((Communication as any).isSuccessful(usersResponse)) {
					const rawUsers = usersResponse.data?.Users ?? usersResponse.data;
					const usersList: User[] = Array.isArray(rawUsers)
						? rawUsers.map((u: any) => ({
								...u,
								RoleLabel: UsersUtils.getRoleUser(u).label,
							}))
						: [];
					setUsers(usersList);
					setActiveUsers(usersList.length);
				}
			} catch {
				// Keep defaults on error
			}
		};

		fetchSeatData();
	}, [currentPlanId]);

	const selectPlan = (planId: number) => {
		if (planId === selectedPlanId) return;

		const prevPlanId = selectedPlanId;
		setSelectedPlanId(planId);

		setSeatsByPlan(prev => {
			const next = { ...prev };

			// Reset the departing plan's seats
			if (prevPlanId === currentPlanId) {
				next[prevPlanId] = currentSeats;
			} else {
				const prevPlan = sortedPlans.find((p: Plan) => p.PlanId === prevPlanId);
				next[prevPlanId] = getIncludedSeats(prevPlan);
			}

			// Set the new plan's seats based on preservation rules
			const newPlanObj = sortedPlans.find((p: Plan) => p.PlanId === planId);
			const newIncluded = getIncludedSeats(newPlanObj);
			const currentPlan = BillingPlans.getPlanByPlanId(currentPlanId);
			const newPlan = BillingPlans.getPlanByPlanId(planId);

			if (planId === currentPlanId) {
				next[planId] = currentSeats;
			} else if (currentPlan && newPlan && newPlan.Price < currentPlan.Price) {
				// Downgrade: fit active users into the new plan's included seats
				next[planId] = Math.max(activeUsers, newIncluded);
			} else {
				// Upgrade: max(currentTotalSeats, newPlan's included)
				next[planId] = Math.max(currentSeats, newIncluded);
			}

			return next;
		});
	};

	const setSeats = (planId: number, count: number) => {
		const plan = sortedPlans.find((p: Plan) => p.PlanId === planId);
		const included = getIncludedSeats(plan);
		const clamped = Math.min(999, Math.max(included, Math.floor(count)));
		if (planId !== selectedPlanId) {
			selectPlan(planId);
		}
		setSeatsByPlan(prev => ({ ...prev, [planId]: clamped }));
	};

	const resetToCurrentPlan = () => {
		setSelectedPlanId(currentPlanId);
		setSeatsByPlan(initSeatsByPlan(sortedPlans, currentPlanId, currentSeats));
		setConfirmationState('idle');
	};

	// True when a closing-state account confirms the same plan they're already on —
	// the only action is undoing the pending close; no plan or seat change is made.
	// Computed here (before confirmPlanChange) so both the function and the derived
	// isOverflow flag share the same definition without a forward reference.
	const isReactivatingSamePlan =
		isCancelled && selectedPlanId === currentPlanId;

	const confirmPlanChange = async (
		activeUsersOverride?: number
	): Promise<boolean> => {
		const selectedPlanData = BillingPlans.getPlanByPlanId(selectedPlanId);
		const currentPlanData = BillingPlans.getPlanByPlanId(currentPlanId);
		if (!selectedPlanData) return false;

		const newSeats = seatsByPlan[selectedPlanId];
		const effectiveActiveUsers = activeUsersOverride ?? activeUsers;
		const isSeatOnly = selectedPlanId === currentPlanId;

		// Skip overflow for same-plan reactivation only when seats haven't been
		// reduced below active users — the seat stepper allows edits even on the
		// current plan, so we can't assume newSeats >= effectiveActiveUsers.
		if (
			!(isReactivatingSamePlan && newSeats >= effectiveActiveUsers) &&
			newSeats < effectiveActiveUsers
		) {
			return false;
		}

		setConfirmationState('confirming');
		setIsLoading(true);

		try {
			if (closeRequestedDate) {
				const reactivateResponse = await (
					Communication as any
				).reactivatePlan();
				if (!(Communication as any).isSuccessful(reactivateResponse)) {
					Logger.logToServer({
						category: 'error_reactivate_plan',
						message: reactivateResponse?.message,
					});
					setConfirmationState('idle');
					return false;
				}
			}

			// Track analytics
			const planChangedProperties = {
				prior_plan_selected: currentPlanData?.Name ?? '',
				prior_monthly_plan_cost: currentPlanData?.Price ?? 0,
				new_plan_selected: selectedPlanData.Name,
				new_monthly_plan_cost: selectedPlanData.Price,
			};
			Analytics.trackEvent(
				'planSelectionChanged',
				planChangedProperties,
				Analytics.agent.segment
			);

			if (!isSeatOnly) {
				const response = await (Communication as any).changePlan({
					PlanId: selectedPlanId,
					PromoCode: null,
					Disposition: {},
					CancelReason: {},
					newPlanPrice: selectedPlanData.Price,
				});

				if (!(Communication as any).isSuccessful(response)) {
					Logger.logToServer({
						category: 'error_change_plan',
						message: response?.message,
					});
					setConfirmationState('idle');
					return false;
				}

				// Backend takes a moment to propagate the pending plan — show it optimistically now.
				setOptimisticPendingPlanId(selectedPlanId);
			}

			// Update seats if changed from current
			if (newSeats !== currentSeats) {
				await AccountCommunication.setMaximumAllowedUsers({
					NewMaximumAllowedUsers: newSeats,
				});
			}

			// Reload account info + billing plans so the UI reflects the new pending/current plan and seats.
			await Promise.all([Session.loadAccountInfo(), BillingPlans.init()]);

			// Promote selected plan to current so UI reflects the change
			setCurrentSeats(newSeats);
			setSeatsByPlan(() =>
				initSeatsByPlan(sortedPlans, selectedPlanId, newSeats)
			);
			setConfirmationState('success');

			setTimeout(() => {
				setConfirmationState('idle');
			}, 4000);

			return true;
		} catch (error) {
			Logger.logToServer({
				category: 'error_change_plan',
				message: error instanceof Error ? error.message : 'Plan change failed',
			});
			setConfirmationState('idle');
			return false;
		} finally {
			setIsLoading(false);
		}
	};

	const deleteUsersAndConfirm = async (userIds: number[]) => {
		if (!userIds.length) return;

		setIsLoading(true);
		try {
			const response = await AccountCommunication.deleteUsers(userIds);
			if (AccountCommunication.isSuccessful(response)) {
				const remainingUsers = users.filter(
					u => userIds.indexOf(u.UserId) === -1
				);
				setUsers(remainingUsers);
				setActiveUsers(remainingUsers.length);

				Logger.logToServer({
					category: 'delete_users_for_plan_change',
					message: `Deleted ${userIds.length} users`,
				});

				// If no longer overflow, proceed with plan change
				const newSeats = seatsByPlan[selectedPlanId];
				if (newSeats >= remainingUsers.length) {
					const ok = await confirmPlanChange(remainingUsers.length);
					if (!ok) {
						throw new Error('PLAN_CHANGE_FAILED');
					}
					return;
				} else {
					throw new Error('PLAN_CHANGE_STILL_OVERFLOW');
				}
			} else {
				throw new Error(response?.message || 'User deletion failed');
			}
		} catch (error) {
			Logger.logToServer({
				category: 'error_delete_users',
				message: 'User deletion failed',
			});
			throw error instanceof Error ? error : new Error('User deletion failed');
		} finally {
			setIsLoading(false);
		}
	};

	// Derived values
	const selectedSeats = seatsByPlan[selectedPlanId] ?? 0;
	const selectedPlan = sortedPlans.find(
		(p: Plan) => p.PlanId === selectedPlanId
	);
	const seatPrice = getSeatPrice(selectedPlan);
	const includedSeats = getIncludedSeats(selectedPlan);
	const additionalSeats = Math.max(0, selectedSeats - includedSeats);
	const professionalAvailable = (BillingPlans.plans || []).some(
		(p: Plan) => p.PlanId === PlanIds.Professional403
	);
	const isOnGbbPlan =
		actualCurrentPlanId === PlanIds.CoreV380 ||
		actualCurrentPlanId === PlanIds.OfficeV381 ||
		actualCurrentPlanId === PlanIds.EcommerceV383;
	// 385 is the legacy multi-location plan; no named constant exists in PlanIds
	const LEGACY_MULTI_LOCATION = 385;
	const isOnLegacyEligiblePlan =
		isOnGbbPlan ||
		(actualCurrentPlanId != null && isPaygPlan(actualCurrentPlanId)) ||
		actualCurrentPlanId === LEGACY_MULTI_LOCATION;
	const isNewLineup =
		BillingPlans.isNewLineupPlan() ||
		(isOnLegacyEligiblePlan && professionalAvailable);
	const planBasePrice = selectedPlan?.Price ?? 0;
	const totalPrice = planBasePrice + additionalSeats * seatPrice;

	// When the user clicks their own legacy current plan, treat it as a no-op:
	// the panel just shows their current plan summary, no Update available.
	const selectedIsLegacyCurrent =
		currentIsLegacy && selectedPlanId === currentPlanId;
	// When the pending plan was auto-selected at registration, confirming any
	// plan (even the same one) is a real action — it clears the auto-selected
	// flag in the backend, so we treat that as a change.
	const hasChanges =
		BillingPlans.pendingPlanWasAutoSelectedOnRegistration ||
		(!selectedIsLegacyCurrent &&
			(selectedPlanId !== currentPlanId || selectedSeats !== currentSeats));
	// isReactivatingSamePlan is defined above confirmPlanChange so both the
	// function body and this derived flag share the same computation.
	const isOverflow =
		isNewLineup &&
		!selectedIsLegacyCurrent &&
		!isReactivatingSamePlan &&
		selectedSeats < activeUsers;
	const isSelectedMultiLocation = isMultiLocationPlan(selectedPlanId);

	const currentPlanObj = sortedPlans.find(
		(p: Plan) => p.PlanId === currentPlanId
	);
	const currentPlanName = currentPlanObj?.Name ?? '';
	const currentPlanPrice = currentPlanObj?.Price ?? 0;
	const selectedPlanPrice = selectedPlan?.Price ?? 0;
	const isUpgrade =
		selectedPlanId !== currentPlanId && selectedPlanPrice > currentPlanPrice;
	const isDowngrade =
		selectedPlanId !== currentPlanId && selectedPlanPrice < currentPlanPrice;

	// Detect if user manually adjusted seats after selecting a different plan
	let isSeatAdjustedAfterPlanChange = false;
	if (selectedPlanId !== currentPlanId) {
		const selectedIncluded = getIncludedSeats(selectedPlan);
		const defaultSeats = isDowngrade
			? Math.max(activeUsers, selectedIncluded)
			: Math.max(currentSeats, selectedIncluded);
		isSeatAdjustedAfterPlanChange = selectedSeats !== defaultSeats;
	}

	return {
		state: {
			plans: sortedPlans,
			currentPlanId,
			currentSeats,
			activeUsers,
			users,
			nextBillingDate,
			selectedPlanId,
			seatsByPlan,
			isLoading,
			confirmationState,
		},
		actions: {
			selectPlan,
			setSeats,
			resetToCurrentPlan,
			confirmPlanChange,
			deleteUsersAndConfirm,
		},
		derived: {
			hasChanges,
			isOverflow,
			isSelectedMultiLocation,
			isUpgrade,
			isDowngrade,
			isSeatAdjustedAfterPlanChange,
			// Only treat the user as "on trial" when they're on Trial401 AND haven't
			// committed to a pending plan (auto-selected-at-registration doesn't count
			// as a real commitment yet).
			isOnTrialPlan:
				BillingPlans.isNewLineUpTrialPlan(actualCurrentPlanId) &&
				(!pendingPlanId ||
					BillingPlans.pendingPlanWasAutoSelectedOnRegistration),
			// "On Professional" means committed to Professional for the next cycle —
			// either pending Professional, or current Professional with no pending
			// change. A pending non-Professional (e.g. Basic) means the user is
			// heading away from Pro, so the banner should treat them as off-Pro.
			isOnProfessional:
				pendingPlanId === PlanIds.Professional403 ||
				(actualCurrentPlanId === PlanIds.Professional403 && !pendingPlanId),
			// User has committed (non-auto-selected) to Basic for the next cycle.
			pendingBasicOnTrial:
				pendingPlanId === PlanIds.Basic402 &&
				!BillingPlans.pendingPlanWasAutoSelectedOnRegistration,
			// User's actual current plan is Basic with no pending change — TRIAL B
			// "Basic picked on pricing page" copy.
			currentIsBasic:
				actualCurrentPlanId === PlanIds.Basic402 && !pendingPlanId,
			pendingIsMultiLocation: pendingPlanId === PlanIds.MultiLocation404,
			currentIsLegacy,
			currentPlanName,
			selectedPlan,
			selectedSeats,
			includedSeats,
			additionalSeats,
			totalPrice,
			seatPrice,
		},
		currentPlanCardRef,
	};
};
